import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Close, RestartAlt, ZoomIn, ZoomOut } from '@mui/icons-material';
import UniversalModal from './UniversalModal';

/** Matches Processing Timeline / UniversalModal close IconButton styling. */
const MODAL_CLOSE_BUTTON_SX = {
  bgcolor: '#f1f5f9',
  '&:hover': { bgcolor: '#e2e8f0' },
};

const normalizeImageItem = (item) => {
  if (!item) return null;
  if (typeof item === 'string') {
    return { src: item, title: 'Image Preview', alt: 'Image Preview' };
  }
  if (!item.src) return null;
  return {
    src: item.src,
    title: item.title || item.alt || 'Image Preview',
    alt: item.alt || item.title || 'Image Preview',
  };
};

export const ImageGalleryDialog = ({
  isOpen,
  onClose,
  images = null,
  initialImageIndex = 0,
  src = '',
  title = 'Image Preview',
  alt = '',
  employeeName = '',
  employeeId = '',
  profileImage = '',
  hideTitle = false,
  resolveImageSrc = null,
}) => {
  const galleryImages = useMemo(() => {
    if (Array.isArray(images) && images.length > 0) {
      if (typeof resolveImageSrc === 'function') {
        return images.map((item) => {
          if (!item) return null;
          if (typeof item === 'string') {
            return { src: item, title: 'Image Preview', alt: 'Image Preview' };
          }
          return {
            src: item.src || '',
            title: item.title || item.alt || 'Image Preview',
            alt: item.alt || item.title || 'Image Preview',
            faceId: item.faceId,
          };
        }).filter(Boolean);
      }
      return images.map(normalizeImageItem).filter(Boolean);
    }
    return src ? [{ src, title, alt }] : [];
  }, [alt, images, resolveImageSrc, src, title]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [resolvedSrcByIndex, setResolvedSrcByIndex] = useState({});
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [imageSize, setImageSize] = useState(null);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const imageRef = useRef(null);

  const applyImageSize = useCallback((img) => {
    if (!img) return;
    const width = Number(img.naturalWidth) || 0;
    const height = Number(img.naturalHeight) || 0;
    if (width > 0 && height > 0) {
      setImageSize({ width, height });
    }
  }, []);

  const activeImage = galleryImages[activeIndex] || null;
  const activeImageSrc = activeImage?.src || resolvedSrcByIndex[activeIndex] || '';
  const hasMultipleImages = galleryImages.length > 1;
  const canGoPrevious = hasMultipleImages && activeIndex > 0;
  const canGoNext = hasMultipleImages && activeIndex < galleryImages.length - 1;
  const counterText = hasMultipleImages ? `Image ${activeIndex + 1} of ${galleryImages.length}` : '';
  const hasEmployeeDetails = Boolean(employeeName || employeeId);
  const employeeInitial = (employeeName || employeeId || '?').charAt(0).toUpperCase();
  const canZoomOut = zoom > 1;
  const canResetZoom = zoom !== 1 || pan.x !== 0 || pan.y !== 0;
  const dimensionsLabel = imageSize
    ? `${imageSize.width} × ${imageSize.height} px`
    : '';

  const updateZoom = useCallback((nextZoom) => {
    setZoom((prev) => {
      const resolved = typeof nextZoom === 'function' ? nextZoom(prev) : nextZoom;
      const clamped = Math.max(1, Math.min(4, Number(resolved) || 1));
      if (clamped === 1) {
        setPan({ x: 0, y: 0 });
      }
      return Number(clamped.toFixed(2));
    });
  }, []);

  const zoomIn = useCallback(() => {
    updateZoom((prev) => prev + 0.25);
  }, [updateZoom]);

  const zoomOut = useCallback(() => {
    updateZoom((prev) => prev - 0.25);
  }, [updateZoom]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const goToPrevious = useCallback(() => {
    if (!canGoPrevious) return;
    setActiveIndex((prev) => Math.max(0, prev - 1));
  }, [canGoPrevious]);

  const goToNext = useCallback(() => {
    if (!canGoNext) return;
    setActiveIndex((prev) => Math.min(galleryImages.length - 1, prev + 1));
  }, [canGoNext, galleryImages.length]);

  useEffect(() => {
    if (isOpen) {
      const safeIndex = Math.max(0, Math.min(Number(initialImageIndex) || 0, Math.max(galleryImages.length - 1, 0)));
      setActiveIndex(safeIndex);
      setResolvedSrcByIndex({});
      setLoading(true);
      setFailed(false);
    }
  }, [galleryImages, initialImageIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setFailed(false);
    setImageSize(null);
    resetZoom();

    if (activeImageSrc) {
      setLoading(true);
      return undefined;
    }
    if (typeof resolveImageSrc !== 'function' || !activeImage) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    resolveImageSrc(activeImage, activeIndex)
      .then((nextSrc) => {
        if (cancelled) return;
        if (!nextSrc) {
          setFailed(true);
          setLoading(false);
          return;
        }
        setResolvedSrcByIndex((prev) => ({ ...prev, [activeIndex]: nextSrc }));
        setLoading(true);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeImage, activeImageSrc, activeIndex, isOpen, resetZoom, resolveImageSrc]);

  useEffect(() => {
    if (!isOpen || loading || failed || !activeImage?.src) return;
    applyImageSize(imageRef.current);
  }, [activeImageSrc, applyImageSize, failed, isOpen, loading]);

  useEffect(() => {
    if (!isOpen || !hasMultipleImages) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && canGoPrevious) {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight' && canGoNext) {
        event.preventDefault();
        goToNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canGoNext, canGoPrevious, goToNext, goToPrevious, hasMultipleImages, isOpen]);

  useEffect(() => {
    if (!dragging) return undefined;
    const handleMouseMove = (event) => {
      setPan({
        x: dragStartRef.current.panX + (event.clientX - dragStartRef.current.x),
        y: dragStartRef.current.panY + (event.clientY - dragStartRef.current.y),
      });
    };
    const handleMouseUp = () => {
      setDragging(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  const handleWheel = useCallback((event) => {
    if (!activeImageSrc) return;
    event.preventDefault();
    updateZoom((prev) => prev + (event.deltaY < 0 ? 0.15 : -0.15));
  }, [activeImageSrc, updateZoom]);

  const handleImageMouseDown = useCallback((event) => {
    if (zoom <= 1) return;
    event.preventDefault();
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setDragging(true);
  }, [pan.x, pan.y, zoom]);

  return (
    <UniversalModal
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      title={(hideTitle || hasEmployeeDetails) ? null : (activeImage?.title || title)}
      header={hasEmployeeDetails ? (
        <Box sx={{ position: 'relative', width: '100%', py: { xs: 0.5, sm: 0.75 } }}>
          <IconButton
            aria-label="Close image preview"
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 0,
              ...MODAL_CLOSE_BUTTON_SX,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 1.25 }}
            alignItems="center"
            justifyContent="flex-start"
            sx={{ pr: { xs: 4, sm: 5 } }}
          >
            <Avatar
              src={profileImage || undefined}
              alt={employeeName || employeeId || 'Employee'}
              sx={{
                width: { xs: 38, sm: 44 },
                height: { xs: 38, sm: 44 },
                bgcolor: '#EEF2FF',
                color: '#374151',
                fontWeight: 700,
                fontSize: { xs: '0.92rem', sm: '1.05rem' },
                flexShrink: 0,
              }}
            >
              {employeeInitial}
            </Avatar>
            <Stack spacing={0.1} justifyContent="center" sx={{ minWidth: 0 }}>
              {employeeName ? (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    textAlign: 'left',
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                  }}
                  noWrap
                >
                  {employeeName}
                </Typography>
              ) : null}
              {employeeId ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'left', fontSize: { xs: '0.66rem', sm: '0.72rem' } }}
                  noWrap
                >
                  {employeeId}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      ) : undefined}
      headerRight={!hasEmployeeDetails ? (
        <IconButton
          aria-label="Close image preview"
          onClick={onClose}
          size="small"
          sx={MODAL_CLOSE_BUTTON_SX}
        >
          <Close fontSize="small" />
        </IconButton>
      ) : undefined}
      footer={(
        <Box sx={{ position: 'relative', width: '100%', minHeight: 36 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 36 }}>
            <IconButton size="small" aria-label="Zoom out" onClick={zoomOut} disabled={!canZoomOut}>
              <ZoomOut fontSize="small" />
            </IconButton>
            <Button size="small" startIcon={<RestartAlt fontSize="small" />} onClick={resetZoom} disabled={!canResetZoom}>
              Reset
            </Button>
            <IconButton size="small" aria-label="Zoom in" onClick={zoomIn} disabled={zoom >= 4}>
              <ZoomIn fontSize="small" />
            </IconButton>
          </Stack>
          <Box
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              right: 0,
              top: '50%',
              transform: { xs: 'none', sm: 'translateY(-50%)' },
              display: 'flex',
              justifyContent: 'flex-end',
              mt: { xs: 1, sm: 0 },
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      )}
      contentDividers
      paperProps={{
        sx: {
          width: { xs: 'calc(100vw - 24px)', sm: '80vw' },
          height: { xs: 'calc(100vh - 24px)', sm: '80vh' },
          maxWidth: '1280px',
          maxHeight: '900px',
          display: 'flex',
        },
      }}
      titleSx={{
        py: 1.5,
        '& .MuiTypography-root': {
          fontSize: { xs: '1rem', sm: '1.1rem' },
        },
      }}
      contentSx={{
        p: { xs: 1.25, sm: 2 },
        bgcolor: 'grey.50',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      actionsSx={{ py: 1.5, px: 2 }}
    >
      <Box
        data-image-preview-dialog="true"
        onWheel={handleWheel}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          bgcolor: 'transparent',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {dimensionsLabel ? (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 12 },
              left: { xs: 8, sm: 12 },
              zIndex: 4,
              px: 1,
              py: 0.35,
              borderRadius: 1,
              bgcolor: 'rgba(15, 23, 42, 0.72)',
              color: '#fff',
              fontWeight: 700,
              letterSpacing: 0.3,
              fontSize: { xs: '0.68rem', sm: '0.75rem' },
              lineHeight: 1.3,
              pointerEvents: 'none',
            }}
          >
            {dimensionsLabel}
          </Typography>
        ) : null}
        {hasMultipleImages ? (
          <>
            <IconButton
              aria-label="Previous image"
              onClick={goToPrevious}
              disabled={!canGoPrevious}
              sx={{
                position: 'absolute',
                left: { xs: 6, sm: 12 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.14)',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: '#f8fafc' },
                '&.Mui-disabled': {
                  bgcolor: '#f8fafc',
                  opacity: 0.45,
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              aria-label="Next image"
              onClick={goToNext}
              disabled={!canGoNext}
              sx={{
                position: 'absolute',
                right: { xs: 6, sm: 12 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.14)',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: '#f8fafc' },
                '&.Mui-disabled': {
                  bgcolor: '#f8fafc',
                  opacity: 0.45,
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        ) : null}
        {hasMultipleImages ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              position: 'absolute',
              bottom: { xs: 8, sm: 12 },
              left: 0,
              right: 0,
              zIndex: 3,
              textAlign: 'center',
              fontWeight: 600,
              pointerEvents: 'none',
            }}
          >
            {counterText}
          </Typography>
        ) : null}
        {activeImageSrc && !failed ? (
          <>
            {loading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : null}
            <Box
              component="img"
              src={activeImageSrc}
              alt={activeImage?.alt || activeImage?.title || title}
              ref={imageRef}
              draggable={false}
              onMouseDown={handleImageMouseDown}
              onLoad={(event) => {
                setLoading(false);
                applyImageSize(event.currentTarget);
              }}
              onError={() => {
                setLoading(false);
                setFailed(true);
                setImageSize(null);
              }}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 1,
                boxShadow: loading ? 'none' : '0 8px 24px rgba(15, 23, 42, 0.18)',
                opacity: loading ? 0 : 1,
                cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: dragging ? 'none' : 'opacity 160ms ease-in-out, transform 120ms ease-out',
                userSelect: 'none',
              }}
            />
          </>
        ) : (
          <Typography color="text.secondary">
            {failed ? 'Unable to load image.' : 'Loading image...'}
          </Typography>
        )}
      </Box>
    </UniversalModal>
  );
};

export default ImageGalleryDialog;
